/* eslint-disable no-undef */
import Vue from 'vue';
import Component from 'vue-class-component';
import {Watch} from 'vue-property-decorator';
import {mapGetters, mapState, mapActions} from 'vuex';
import {getSpeedUnit, getAccelerationUnit, getJerkUnit} from '../../helpers/UnitHelpers';

import Block from './../../components/Block';
import IconLink from './../../components/IconLink';
import Popover from './../../components/Popover';
import Modal from './../../components/Modal';
import TopSuccess from './../../components/TopSuccess';
import TopWarning from './../../components/TopWarning.vue';
import TopError from './../../components/TopError';
import InputNumbers from './../../components/InputNumbers';
import Tooltip from './../../components/Tooltip';

import InertiaTool from '@/components/InertiaTool.vue';
import RthTool from '@/components/RthTool.vue';
import MaxJerkTool from '@/components/MaxJerkTool.vue';

import {isTorqueUnit, isSI, isImperial} from '../../helpers';

import Scenario from '../../models/Scenario';
import Motor from '../../models/Motor';
import Results from '../../models/Results';
import MovementProfile from '../../models/MovementProfile';
import MotorTypeSelector from '../../components/MotorSelector';
import Motors from '../Motors';

import Qty from 'js-quantities';

let Highcharts = require('highcharts/highstock');
import 'highcharts/css/highcharts.css';

let self = undefined;

const DECIMAL_PLACES = 4;
const MAX_CHARTS = 2;

@Component({
    props: {
        id: {
            type: String,
            default: '-1',
        },
    },
    computed: mapState({
        types: state => state.scenarios.movement_types,
        power_supplies: state => state.scenarios.power_supplies,
        motors: state => state.motor_types,
        unit: state => state.unit,
        scenarios: state => state.scenarios.scenarios,
        newScenario: state => state.scenarios.new_scenario,
        unit_profiles: state => state.scenarios.unit_profiles,
        shared: state => state.scenarios.shared,
    }),
    components: {
        InertiaTool,
        RthTool,
        MaxJerkTool,
    },
    methods: {
        ...mapActions(['saveScenario']),
    },
    beforeRouteUpdate(to, from, next) {
        this.routeUpdate(to, from, next);
    },

    beforeRouteLeave(to, from, next) {
        this.routeLeave(to, from, next);
    },
})
export default class Profiles extends Vue {
    name = 'profiles';

    /**
     * State for the selected movement profile
     * @type {boolean}
     */
    selected = false;
    selectedProfile = {};
    selectedAppData = false;

    selected_unit = 'si';

    draggable = false;

    adding = false;
    addIndex = -1;

    help = false;
    helpMessage = '';
    helpContact = '';
    requestingHelp = false;

    selectedGraph = 'time';
    selectedChartItem = undefined;
    fvName = 'F/v';
    tvName = 'T/n';
    fvMotorName = 'F/v Motor';
    tvMotorName = 'T/n Motor';
    graphs = {
        at: 'Acceleration',
        ft: 'Force',
        it: 'Current',
        jt: 'Jerk',
        pt: 'Power',
        ut: 'Voltage',
        vt: 'Velocity',
        xt: 'Movement',
        tt: 'Temperature',
        fapp: 'F/v',
        fmot: 'F/v Motor',
    };
    resetGraphs = true;
    groupedGraphs = [
        this.fvName.toLowerCase(),
        this.fvMotorName.toLowerCase(),
        this.tvName.toLowerCase(),
        this.tvMotorName.toLowerCase(),
    ];

    motorSelected = false;

    largeGraphs = ['jerk'];

    visibleGraphs = {movement: true};
    selectedGraphs = ['movement'];
    lastSelectedGraph = 'movement';

    editAppData = false;

    changeMotor = false;
    motorSelection = false;
    selectedMotorType = null;

    scenario = new Scenario();
    newProfile = new MovementProfile();

    showDeleteConfirmation = false;
    showNavigationDialog = false;

    scenarioName = '';

    edit = false;
    saveAs = false;

    serie = {};

    hasScrollbar = false;
    hasGradientLeft = false;
    hasGradientRight = false;

    appDataValidationError = false;

    warning = '';
    errorMessage = '';
    successMessage = '';
    ctaMessage = '';

    hasChanges = false;

    isShared = false;
    share = false;
    requestingShare = false;
    shareMessage = '';

    searched = false;

    originalOrder = [];

    showQuoteDialog = false;
    quoteMessage = '';
    selectedScenario = -1;
    requestingQuote = false;

    /* eslint-disable no-useless-escape */
    emailPattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    showShareInformation = false;
    switching = false;

    get scenarioID() {
        if (isNaN(this.id)) {
            return -1;
        }

        return Number(this.id);
    }

    set scenarioID(newVal) {
        this.id = newVal;
    }

    get profiles() {
        if (this.scenario === undefined) {
            return [];
        }

        return this.scenario.profiles;
    }

    set profiles(profiles) {
        this.scenario.profiles = profiles;
    }

    // Properties are the localized properties of the motor series
    get properties() {
        let properties;
        if (this.serie && this.serie.properties) {
            if (this.serie.properties[this.$i18n.locale]) {
                properties = this.serie.properties[this.$i18n.locale];
            } else if (this.serie.properties[this.$i18n.fallbackLocale]) {
                properties = this.serie.properties[this.$i18n.fallbackLocale];
            }
        }
        return properties || {};
    }

    get specs() {
        return this.properties.specs || this.serie.specs || [];
    }

    get seriesName() {
        return this.properties.name || this.serie.name || '';
    }

    get subtitle() {
        return this.properties.subtitle || this.serie.subtitle || '';
    }

    /**
     * Application data getters
     * @returns {*}
     */
    updateTempHeatsink() {
        this.onInputBlur(this.$refs.tempHeatsink, this.scenario.applicationData.tempHeatsink);
    }

    updateMovingMass() {
        this.onInputBlur(this.$refs.movingMass, this.scenario.applicationData.movingMass);
    }

    updateFrictionForce() {
        this.onInputBlur(this.$refs.frictionForce, this.scenario.applicationData.frictionForce);
    }

    updateInclinementAngle() {
        this.onInputBlur(
            this.$refs.inclinementAngle,
            this.scenario.applicationData.inclinementAngle,
        );
    }

    updatePowerSupply() {
        this.onInputBlur(
            this.$refs.powerSupply,
            this.scenario.applicationData.quickEditPowerSupply,
        );
    }

    updateFriction() {
        this.onInputBlur(this.$refs.friction, this.scenario.applicationData.friction);
    }

    updateRthermHeatsink() {
        this.onInputBlur(this.$refs.rtherm, this.scenario.applicationData.rtherm_heatsink);
    }

    updateLoadInertia() {
        this.onInputBlur(this.$refs.loadInertia, this.scenario.applicationData.loadInertia);
    }

    /**
     * Getters for new movement profile values
     */
    updateNewMovingDistance() {
        this.onInputBlur(this.$refs.newMovingDistance, this.newProfile.movingDistance);
    }

    updateNewTorqueDistance() {
        this.onInputBlur(this.$refs.newMovingDistance, this.newProfile.torqueDistance);
    }

    updateNewSystemTorque() {
        this.onInputBlur(this.$refs.newTorque, this.newProfile.torque);
    }

    updateNewMovingTime() {
        this.onInputBlur(this.$refs.newMovingTime, this.newProfile.movingTime);
    }

    updateNewAccelerationTime() {
        this.onInputBlur(this.$refs.newAccelerationTime, this.newProfile.accelerationTime);
    }

    updateNewAccelerationTorque() {
        this.onInputBlur(this.$refs.newAccelerationTorque, this.newProfile.accelerationTorque);
    }

    updateNewMaxJerk() {
        this.onInputBlur(this.$refs.newMaxJerk, this.newProfile.maxJerk);
    }

    updateNewLoad() {
        this.onInputBlur(this.$refs.newLoad, this.newProfile.load);
    }

    updateNewMaxSpeed() {
        this.onInputBlur(this.$refs.newMaxSpeed, this.newProfile.maxSpeed);
    }

    updateNewTorqueSpeed() {
        this.onInputBlur(this.$refs.newTorqueSpeed, this.newProfile.torqueSpeed);
    }

    updateNewAcceleration() {
        this.onInputBlur(this.$refs.newAcceleration, this.newProfile.acceleration);
    }

    updateNewPeriod() {
        this.onInputBlur(this.$refs.newPeriod, this.newProfile.period);
    }

    updateNewLabel() {
        this.onInputBlur(this.$refs.newLabel, this.newProfile.label);
    }

    /**
     * Update input values for editing a selected movement profile
     */
    updateMovingDistance() {
        this.onInputBlur(this.$refs.movingDistance, this.selectedProfile.movingDistance);
    }

    updateTorqueDistance() {
        this.onInputBlur(this.$refs.movingDistance, this.selectedProfile.torqueDistance);
    }

    updateTorque() {
        this.onInputBlur(this.$refs.torque, this.selectedProfile.torque);
    }

    updateMovingTime() {
        this.onInputBlur(this.$refs.movingTime, this.selectedProfile.movingTime);
    }

    updateAccelerationTime() {
        this.onInputBlur(this.$refs.accelerationTime, this.selectedProfile.accelerationTime);
    }

    updateAccelerationTorque() {
        this.onInputBlur(this.$refs.accelerationTorque, this.selectedProfile.accelerationTorque);
    }

    updateMaxJerk() {
        this.onInputBlur(this.$refs.maxJerk, this.selectedProfile.maxJerk);
    }

    updateMaxJerkFromCalculation(maxJerk) {
        this.selectedProfile.maxJerk = maxJerk;
        this.showMaxJerkTool = !this.showMaxJerkTool;
    }

    updateMaxVoltsFromCalculation(maxVolts) {
        this.selectedProfile.maxVolts = maxVolts;
    }

    updateNewMaxJerkFromCalculation(maxJerk) {
        this.newProfile.maxJerk = maxJerk;
        this.showMaxJerkTool = !this.showMaxJerkTool;
    }

    updateNewMaxVoltsFromCalculation(maxVolts) {
        this.newProfile.maxVolts = maxVolts;
    }

    updateLoad() {
        this.onInputBlur(this.$refs.load, this.selectedProfile.load);
    }

    updateMaxSpeed() {
        this.onInputBlur(this.$refs.maxSpeed, this.selectedProfile.maxSpeed);
    }

    updateTorqueSpeed() {
        this.onInputBlur(this.$refs.torqueSpeed, this.selectedProfile.torqueSpeed);
    }

    updateAcceleration() {
        this.onInputBlur(this.$refs.acceleration, this.selectedProfile.acceleration);
    }

    updatePeriod() {
        this.onInputBlur(this.$refs.period, this.selectedProfile.period);
    }

    updateLabel() {
        this.onInputBlur(this.$refs.label, this.selectedProfile.label);
    }

    /**
     * Set the updated value as soon as an input blurs to reflect the unit
     * @param field
     */
    onInputBlur(field, value) {
        if (field !== undefined) {
            field.value = value;
        }
    }

    created() {
        this.getMovementTypes();
        // this.getPowerSupplies();
        this.getScenario();
    }

    mounted() {
        this.shareTokenChanged();

        this.$store.dispatch('activateScenario', this.scenarioID);

        document.addEventListener('save-scenario', this.showSaveDialog);
        document.addEventListener('unit-changed', this.unitChangedListener);

        this.$eventHub.$on('request-pdf', this.pdfListener);
        this.$eventHub.$on('request-quote', this.getQuoteDialog);
        this.$eventHub.$on('request-help', this.getHelpDialog);
        this.$eventHub.$on('request-share', this.getShareDialog);
        this.$eventHub.$on('language-changed', this.drawChart);
        this.$eventHub.$on('confirm-navigation', e => {
            let next = e.next;
            this.navigationNext = next;
            this.showNavigationDialog = true;
        });
        this.$eventHub.$on('scenario-simulated', this.recheckRtherm);

        //Confirm reload if there are unsaved changes
        window.onbeforeunload = () => {
            if (this.hasChanges && !this.scenario.motor.isEmpty()) {
                return 'You have unsaved changes';
            }
        };
    }

    unitChangedListener(e) {
        this.unitChanged(e.detail.unit);
    }

    pdfListener(e) {
        let name = e.name;
        if (name !== undefined) {
            this.scenario.name = name;
        }

        if (this.scenario) {
            this.scenario
                .pdf(this.unit)
                .then(() => {
                    this.$eventHub.$emit('request-pdf-complete');
                })
                .catch(err => {
                    this.$eventHub.$emit('request-pdf-complete');
                });
        }
    }

    beforeDestroy() {
        document.removeEventListener('save-scenario', this.showSaveDialog);
        document.removeEventListener('unit-changed', this.unitChangedListener);

        this.$eventHub.$off('request-pdf', this.pdfListener);
        this.$eventHub.$off('request-quote', this.getQuoteDialog);
        this.$eventHub.$off('request-help', this.getHelpDialog);
        this.$eventHub.$off('request-share', this.getShareDialog);
        this.$eventHub.$off('language-changed', this.drawChart);

        this.$eventHub.$emit('activate-scenario', {
            scenario: undefined,
            id: -1,
        });
        this.$store.dispatch('newScenario');
    }

    getMovementTypes() {
        Vue.api
            .get('/motion_profiles/')
            .send()
            .then(profiles => {
                this.$store.dispatch('setMovementTypes', profiles);
                MovementProfile.motion_profiles = profiles;
            })
            .catch(err => console.error(err));
    }

    showSaveDialog() {
        this.saveAs = true;
    }

    saveScenarioAs() {
        if (this.scenarioName !== undefined && this.scenarioName.length > 0) {
            this.scenario
                .saveAs(this.scenarioName)
                .then(scenario => {
                    this.hasChanges = false;
                    this.saveScenario(scenario.toJson());
                    const path = '/scenarios/' + scenario.id;
                    if (this.$route.path !== path) this.$router.push(path);

                    this.successCta(this.$t('common.modals.successMessages.sceneSaved'), 5000);
                })
                .catch(err => {
                    console.error('[ save ] Error while saving scenario', err);
                });
            this.closeSaveAsDialog();
        }
    }

    saveCurrentScenario() {
        this.scenario
            .save()
            .then(scenario => {
                this.hasChanges = false;
                this.successCta(this.$t('common.modals.successMessages.sceneSaved'), 5000);
                this.saveScenario(scenario.toJson());
            })
            .catch(err => {
                console.warn('[ save ] Error saving current scenario', err);
            });
        this.closeSaveAsDialog();
    }

    get isSaved() {
        return this.scenarioID !== -1;
    }

    simulate() {
        if (
            this.scenario !== undefined &&
            !this.scenario.applicationData.isEmpty() &&
            this.scenario.profiles.length > 0 &&
            Object.keys(this.scenario.motor).length > 0 &&
            this.scenario.motor.constructor === Motor &&
            !this.scenario.motor.isEmpty()
        ) {
            if (
                (this.scenario.isTorque() && isTorqueUnit(this.unit)) ||
                (!this.scenario.isTorque() && !isTorqueUnit(this.unit))
            ) {
                this.scenario
                    .simulate(this.unit)
                    .then(scenario => {
                        this.$eventHub.$emit('scenario-simulated');

                        if (this.scenario.isTorque()) {
                            this.graphs.ft = 'Torque';
                            this.graphs.fapp = this.tvName;
                            this.graphs.fmot = this.tvMotorName;
                        } else {
                            this.graphs.ft = 'Force';
                            this.graphs.fapp = this.fvName;
                            this.graphs.fmot = this.fvMotorName;
                        }

                        if (!this.scenario.hasTemperatureData()) {
                            this.$eventHub.$emit('scenario-error');
                            this.error(this.$t('profiles.errorTempSimulation'), 7000);
                        }

                        this.drawChart();
                    })
                    .catch(err => {
                        this.scenario.results = new Results();
                        this.scenario.graphs = {};
                        this.scenario.times = {};

                        this.drawChart();
                        this.$eventHub.$emit('scenario-error');
                        this.error(this.$t('profiles.inputError'), 10000);
                    });
            }
        } else {
            this.$eventHub.$emit('scenario-invalid');
        }
    }

    drawChart() {
        self = this;

        (function (H) {
            H.seriesTypes.line.prototype.requireSorting = false;
        })(Highcharts);

        if (this.resetGraphs) {
            this.selectedGraphs = ['movement'];
            this.visibleGraphs = {movement: true};
            this.resetGraphs = false;
        }

        if (this.scenario !== undefined && this.scenario.graphs !== undefined) {
            let sets = this.getTimeSeries();
            sets = sets.concat(this.getSpeedSeries());

            Highcharts.dateFormat();

            let secondTitle = '';
            if (this.selectedGraph === 'time') {
                let length = this.selectedGraphs.length;
                if (length <= 1) {
                    secondTitle = '';
                } else if (length === 2) {
                    secondTitle = this.getLastSelectedGraph();
                }
            }

            Highcharts.setOptions({
                lang: {
                    resetZoom: this.$t('chart.resetZoom'),
                    resetZoomTitle: this.$t('chart.resetZoom'),
                },
            });

            let options = {
                chart: {
                    type: 'line',
                    animation: {
                        duration: 0,
                    },
                    zoomType: 'xy',
                    panKey: 'alt',
                    panning: true,
                    styledMode: true,
                },
                credits: {
                    enabled: false,
                },
                xAxis: [
                    {
                        crosshair: {
                            snap: false,
                            enabled: true,
                        },
                        title: {
                            text:
                                this.selectedGraph === 'time'
                                    ? this.$t('chart.time')
                                    : this.$t('chart.speed') +
                                    '(' +
                                    self.getUnitForName(
                                        'velocity',
                                        self.scenario.unit_profile || Scenario.unitProfile,
                                    ) +
                                    ')',
                        },
                    },
                    {
                        crosshair: {
                            snap: false,
                            enabled: true,
                        },
                        title: {
                            text: this.$t('chart.speed'),
                        },
                        visible: false,
                    },
                ],
                yAxis: [
                    {
                        title: {
                            text: this._getLeftYAxisTitle(),
                        },
                        crosshair: {
                            snap: false,
                            enabled: true,
                        },
                        labels: {
                            formatter: function () {
                                let length = self.selectedGraphs.length;
                                let last;

                                if (length === 1) {
                                    last = self.selectedGraphs[0];
                                } else if (length === 2) {
                                    //If two graphs are plotted, show the first selected on the left axis.
                                    last = self.selectedGraphs[0];
                                } else {
                                    // Otherwise show the last selected
                                    last = self.getLastSelectedGraph();
                                }

                                return (
                                    this.value +
                                    ' ' +
                                    self.getUnitForName(
                                        last.toLowerCase(),
                                        self.scenario.unit_profile || Scenario.unitProfile,
                                    )
                                );
                            },
                        },
                    },
                    {
                        title: {
                            text: secondTitle && this.$t('chart.' + secondTitle),
                        },
                        crosshair: {
                            snap: false,
                            enabled: true,
                        },
                        opposite: true,
                        labels: {
                            formatter: function () {
                                let length = self.selectedGraphs.length;
                                let last;
                                if (length === 1) {
                                    last = self.getLastSelectedGraph();
                                } else if (length === 2) {
                                    //If two graphs are plotted, show the first selected on the left axis.
                                    last = self.getLastSelectedGraph();
                                } else if (length > 2) {
                                    // Otherwise show the last selected
                                    return this.value;
                                } else if (length < 2) {
                                    return '';
                                }

                                return (
                                    this.value +
                                    ' ' +
                                    self.getUnitForName(
                                        last.toLowerCase(),
                                        self.scenario.unit_profile || Scenario.unitProfile,
                                    )
                                );
                            },
                        },
                    },
                ],
                title: {
                    text: '',
                    style: {
                        display: 'none',
                    },
                },
                subtitle: {
                    text: '',
                    style: {
                        display: 'none',
                    },
                },
                legend: {
                    title: {
                        text: this.$t('profiles.selectGraphs'),
                        style: {
                            fontWeight: 'bold',
                        },
                    },
                    itemStyle: {
                        fontSize: '14px',
                        fontWeight: 'bold',
                    },
                },
                plotOptions: {
                    series: {
                        animation: {
                            duration: 0,
                        },
                        events: {
                            legendItemClick: (function (self) {
                                return function (event) {
                                    if (!this.visible && self.selectedGraphs.length >= MAX_CHARTS) {
                                        // Allow selection if the count is less than 2, otherwise prevent selection
                                        return false;
                                    }
                                };
                            })(this),
                            show: event => {
                                var series = event.target.yAxis.series,
                                    seriesLen = series.length,
                                    visible = event.target.visible,
                                    id = event.target.userOptions.id;

                                this.setVisible(id, true);
                                if (
                                    id.toLowerCase() === this.fvName.toLowerCase() ||
                                    (id.toLowerCase() === this.tvName.toLowerCase() &&
                                        this.selectedGraph === 'time')
                                ) {
                                    this.switchGraphs('speed', id);
                                }

                                if (
                                    id.toLowerCase() !== this.fvName.toLowerCase() &&
                                    id.toLowerCase() !== this.tvName.toLowerCase() &&
                                    this.selectedGraph === 'speed'
                                ) {
                                    this.switchGraphs('time', id);
                                }
                            },
                            hide: event => {
                                var series = event.target.yAxis.series,
                                    seriesLen = series.length,
                                    visible = event.target.visible,
                                    id = event.target.userOptions.id;

                                this.setVisible(id, false);
                                if (
                                    id.toLowerCase() !== this.fvName.toLowerCase() &&
                                    id.toLowerCase() !== this.tvName.toLowerCase() &&
                                    this.selectedGraph === 'speed'
                                ) {
                                    this.selectedGraph = 'time';
                                }
                            },
                        },
                    },
                },
                tooltip: {
                    formatter: function () {
                        let id = this.series.userOptions.id;

                        if (!self.multiGraph) {
                            if (self.selectedGraph === 'speed') {
                                return (
                                    Number(this.x).toFixed(DECIMAL_PLACES) +
                                    ' ' +
                                    self.getUnitForName(
                                        'velocity',
                                        self.scenario.unit_profile || Scenario.unitProfile,
                                    ) +
                                    ' <br/>' +
                                    Number(this.y).toFixed(DECIMAL_PLACES) +
                                    ' ' +
                                    self.getUnitForName(
                                        id,
                                        self.scenario.unit_profile || Scenario.unitProfile,
                                    )
                                );
                            } else if (id.toLowerCase() === 'power') {
                                return (
                                    self.$t('chart.profile') +
                                    ' ' +
                                    self.getProfileNumber(this.x) +
                                    '<br/>' +
                                    Number(this.x).toFixed(DECIMAL_PLACES) +
                                    ' s <br/>' +
                                    Number(this.y).toFixed(DECIMAL_PLACES) +
                                    ' ' +
                                    self.getUnitForName(
                                        id,
                                        self.scenario.unit_profile || Scenario.unitProfile,
                                    )
                                );
                            } else {
                                return (
                                    self.$t('chart.profile') +
                                    ' ' +
                                    self.getProfileNumber(this.x) +
                                    '<br/>' +
                                    Number(this.x).toFixed(DECIMAL_PLACES) +
                                    ' s <br/>' +
                                    Number(this.y).toFixed(DECIMAL_PLACES) +
                                    ' ' +
                                    self.getUnitForName(
                                        id,
                                        self.scenario.unit_profile || Scenario.unitProfile,
                                    )
                                );
                            }
                        } else {
                            return (
                                Number(this.x).toFixed(DECIMAL_PLACES) +
                                ' <br/>' +
                                Number(this.y).toFixed(DECIMAL_PLACES)
                            );
                        }
                    },
                },
                series: this.buildSeries(sets),
            };

            if (this.selectedGraph === 'speed' && this.scenario.isTorque()) {
                options.xAxis[0].plotLines = [
                    {
                        color: '#e8e8e8',
                        dashStyle: 'solid',
                        value: 0,
                        width: 2,
                    },
                ];
            }
            this.chart = Highcharts.chart('result_chart', options);
        }
    }

    _getLeftYAxisTitle() {
        if (this.selectedGraph === 'speed') {
            if (this.scenario.isTorque()) {
                return `${this.$t('chart.torque')} (${this.getUnitForName(
                    'torque',
                    self.scenario.unit_profile || Scenario.unitProfile,
                )})`;
            } else {
                return `${this.$t('chart.force')} (${this.getUnitForName(
                    'force',
                    self.scenario.unit_profile || Scenario.unitProfile,
                )})`;
            }
        }

        return 0 < this.selectedGraphs.length && this.selectedGraphs.length <= 2
            ? this.$t('chart.' + this.selectedGraphs[0].toLowerCase())
            : this.$t('chart.' + this.getLastSelectedGraph().toLowerCase());
    }

    getProfileNumber(x) {
        let times = this.scenario.times;
        let total = 0;
        for (let k in times) {
            let entry = times[k];
            total += entry;

            if (x <= total) {
                let profile = this.scenario.profiles[k];
                return Number(k) + 1 + ' - ' + profile.type;
            }
        }

        return this.$t('chart.unknown');
    }

    getLastSelectedGraph() {
        if (this.selectedGraphs !== undefined && this.selectedGraphs.length > 0) {
            let last = this.selectedGraphs[this.selectedGraphs.length - 1];
            return last;
        } else {
            return 'movement';
        }
    }

    switchGraphs(to = 'time', name) {
        this.switching = true;

        if (to === 'time') {
            this.chart.xAxis[0].setTitle({
                text: this.$t('chart.time'),
            });

            this.chart.yAxis[1].update({
                visible: true,
            });

            if (this.scenario.isTorque()) {
                this.chart.get(this.tvName.toLowerCase()).setVisible(false, false);
            } else {
                this.chart.get(this.fvName.toLowerCase()).setVisible(false, false);
            }
        } else {
            for (let key in this.visibleGraphs) {
                let value = this.visibleGraphs[key];

                if (value && this.groupedGraphs.indexOf(key.toLowerCase()) < 0) {
                    if (typeof this.chart.get(key) !== 'undefined')
                        this.chart.get(key).setVisible(false, false);
                    this.visibleGraphs[key] = false;
                }
            }

            this.chart.xAxis[0].update({
                title: {
                    text: this.$t('chart.speed'),
                },
            });

            this.chart.yAxis[1].update({
                visible: false,
            });
        }

        this.selectedGraph = to;
        this.chart.redraw();
        console.warn(name, this.chart.xAxis);
        this.chart.xAxis[0].update({
            lineWidth: 900,
            title: {
                text: this.$t('chart.speed'),
            },
        });
    }

    setVisible(id, visible) {
        this.visibleGraphs[id.toLowerCase()] = visible;

        let prevLength = this.selectedGraphs.length;

        // Is a new graph made visible?
        if (visible) {
            let previous = this.selectedGraphs[this.selectedGraphs.length - 1];
            let first = this.selectedGraphs[0];
            this.selectedGraphs.push(id);
            let nextLength = this.selectedGraphs.length;

            //No graphs have been selected yet.
            if (prevLength === 0) {
                this.chart.get(id.toLowerCase()).update({
                    yAxis: 0,
                });

                this.chart.yAxis[0].update({
                    title: {
                        text: this.$t(`chart.${id.toLowerCase()}`),
                    },
                });
            } else if (prevLength === 1) {
                // A second graph was toggled visible.

                //Set the newly selected graph to the right axis
                this.chart.get(id.toLowerCase()).update({
                    yAxis: 1,
                });

                this.chart.yAxis[1].update({
                    title: {
                        text: this.$t(`chart.${id.toLowerCase()}`),
                    },
                });

                if (typeof this.chart.get(first.toLowerCase()) !== 'undefined') {
                    this.chart.get(first.toLowerCase()).update({
                        yAxis: 0,
                    });
                }

                this.chart.yAxis[0].update({
                    title: {
                        text: this.$t(`chart.${first.toLowerCase()}`),
                    },
                });
            } else if (prevLength > 1) {
                if (prevLength > 2) {
                    //There are more than 2 graphs selected

                    if (previous !== undefined) {
                        // Move the previously selected graph to the right axis ->
                        this.chart.get(previous.toLowerCase()).update({
                            yAxis: 1,
                        });
                    }
                } else {
                    if (first !== undefined) {
                        // Move the previously selected graph to the right axis ->
                        this.chart.get(first.toLowerCase()).update({
                            yAxis: 1,
                        });
                    }
                }

                this.chart.yAxis[1].update({
                    title: {
                        text: '',
                    },
                });

                //Set the current selected graph to the left y-axis
                this.chart.get(id.toLowerCase()).update({
                    yAxis: 0,
                });

                //Set the name of the current graph as the label for the left y-axis
                this.chart.yAxis[0].update({
                    title: {
                        text: this.$t(`chart.${id.toLowerCase()}`),
                    },
                });
            }
        } else if (this.selectedGraphs.length > 0) {
            //Remove deselected graph from the list.
            let index = this.selectedGraphs.indexOf(id);
            if (index === this.selectedGraphs.length - 1) {
                //Move the axis to the previously selected
                let last = this.selectedGraphs[index];
                this.selectedGraphs.pop();

                let newGraph = this.selectedGraphs[this.selectedGraphs.length - 1];

                this.chart.get(last.toLowerCase()).update({
                    yAxis: 0,
                });

                if (newGraph !== undefined) {
                    this.chart.get(newGraph.toLowerCase()).update({
                        yAxis: 0,
                    });
                    this.chart.yAxis[0].update({
                        title: {
                            text: this.$t(`chart.${newGraph.toLowerCase()}`),
                        },
                    });
                }
            } else {
                this.selectedGraphs.splice(index, 1);
            }

            if (this.selectedGraphs.length === 2) {
                let previous = this.selectedGraphs[this.selectedGraphs.length - 1];
                let first = this.selectedGraphs[0];

                this.chart.yAxis[1].update({
                    title: {
                        text: this.$t(`chart.${previous.toLowerCase()}`),
                    },
                });

                this.chart.yAxis[0].update({
                    title: {
                        text: this.$t(`chart.${first.toLowerCase()}`),
                    },
                });
            } else {
                this.chart.yAxis[1].update({
                    title: {
                        text: '',
                    },
                });

                if (this.selectedGraphs.length === 1) {
                    this.chart.yAxis[0].update({
                        title: {
                            text: this.$t(`chart.${this.selectedGraphs[0].toLowerCase()}`),
                        },
                    });

                    this.chart.get(this.selectedGraphs[0].toLowerCase()).update({
                        yAxis: 0,
                    });
                } else if (this.selectedGraphs.length === 0) {
                    this.chart.yAxis[0].update({
                        title: {
                            text: '',
                        },
                    });
                } else {
                    this.chart.yAxis[0].update({
                        title: {
                            text: this.$t(
                                'chart.' +
                                this.selectedGraphs[
                                this.selectedGraphs.length - 1
                                    ].toLowerCase(),
                            ),
                        },
                    });
                }
            }
        }

        if (this.selectedGraphs.length === 0) {
            this.chart.yAxis[0].update({
                title: {
                    text: '',
                },
            });
            this.chart.yAxis[1].update({
                title: {
                    text: '',
                },
            });
        }
    }

    getUnitForName(id, unitProfile) {
        if (unitProfile !== undefined) {
            switch (id.toLowerCase()) {
                case 'acceleration':
                    if (this.scenario.isTorque()) {
                        // return (this.selected_unit.toLowerCase().indexOf('si') > -1 ? 'rad/s' : 'rotation/s') + '²';
                        return this.getUnitProfile(
                            'torque_acceleration_short',
                            unitProfile,
                            'rad/s^2',
                        ).replace('^2', '²');
                    } else {
                        // console.warn('Acceleration unit', this.getUnitProfile('acceleration_short', unitProfile, 'm/s^2').replace('^2', '²'), unitProfile);
                        return this.getUnitProfile(
                            'acceleration_short',
                            unitProfile,
                            'm/s^2',
                        ).replace('^2', '²');
                    }
                case 'force':
                    return this.getUnitProfile('force_short', unitProfile, 'N');
                case 'torque':
                    return this.getUnitProfile('torque_short', unitProfile, 'Nm');
                case 'movement':
                    return this.scenario.isTorque()
                        ? this.getUnitProfile('torque_distance_short', unitProfile, 'rad')
                        : this.getUnitProfile('distance_short', unitProfile, 'm');
                case 'current':
                    return (
                        this.getUnitProfile('current_short', unitProfile, 'A') +
                        '<span class="small">rms</span>'
                    );
                case 'power':
                    return this.getUnitProfile('power_short', unitProfile, 'W');
                case 'voltage':
                    return this.getUnitProfile('voltage_short', unitProfile, 'V');
                case 'velocity':
                    if (this.scenario.isTorque()) {
                        // return this.getUnitProfile('torque_speed_short', unitProfile, (this.selected_unit.toLowerCase().indexOf('si') > -1 ? 'rad/s' : 'rpm'));;
                        return this.getUnitProfile('torque_speed_short', unitProfile, 'rad/s');
                    } else {
                        return this.getUnitProfile('speed_short', unitProfile, 'm/s');
                    }
                case 'temperature':
                    return this.getUnitProfile('temperature_short', unitProfile, '°C');
                case 'jerk':
                    if (this.scenario.isTorque()) {
                        return this.getUnitProfile(
                            'torque_jerk_short',
                            unitProfile,
                            'rad/s^3',
                        ).replace('^2', '³');
                        // return (this.selected_unit.toLowerCase().indexOf('si') > -1 ? 'rad/s' : 'rotation/s') + '³';
                    } else {
                        return this.getUnitProfile('jerk_short', unitProfile, 'm/s^3').replace(
                            '^3',
                            '³',
                        );
                    }
                case this.fvName.toLowerCase():
                    return this.getUnitProfile('force_short', unitProfile, 'N');
                case this.fvMotorName.toLowerCase():
                    return this.getUnitProfile('force_short', unitProfile, 'N');
                case this.tvName.toLowerCase():
                    return this.getUnitProfile('torque_short', unitProfile, 'N*m');
                case this.tvMotorName.toLowerCase():
                    return this.getUnitProfile('torque_short', unitProfile, 'N*m');
            }
        }
    }

    getUnitProfile(key, profile, fallback) {
        if (profile !== undefined && profile[key] !== undefined) {
            return profile[key];
        }
        return fallback;
    }

    getTimeSeries() {
        let sets = [];
        for (let k in this.scenario.graphs) {
            if (
                this.scenario.graphs.hasOwnProperty(k) &&
                k !== 'fapp' &&
                k !== 'fmot' &&
                k !== 'meta'
            ) {
                if (k !== 'tt' || this.scenario.hasTemperatureData()) {
                    let dataset = this.scenario.graphs[k];
                    let name = this.graphs[k];
                    sets.push({
                        name: this.$t(`chart.${name.toLowerCase()}`),
                        id: name,
                        set: dataset,
                    });
                }
            }
        }

        return sets;
    }

    getSpeedSeries() {
        let sets = [];

        sets.push({
            name: this.$t(`chart.${this.graphs['fapp'].toLowerCase()}`),
            id: this.graphs['fapp'],
            set: this.scenario.graphs.fapp,
            visible:
                this.visibleGraphs[this.graphs['fapp'].toLowerCase()] !== undefined
                    ? this.visibleGraphs[this.graphs['fapp'].toLowerCase()]
                    : false,
        });

        sets.push({
            name: this.$t(`chart.${this.graphs['fmot'].toLowerCase()}`),
            id: this.graphs['fmot'],
            set: this.scenario.graphs.fmot,
            visible:
                this.visibleGraphs[this.graphs['fmot'].toLowerCase()] !== undefined
                    ? this.visibleGraphs[this.graphs['fmot'].toLowerCase()]
                    : false,
        });

        return sets;
    }

    @Watch('selectedGraph')
    updateChart() {
        self = this;
        this.drawChart();
    }

    buildSeries(datasets = []) {
        if (datasets.length > 1) {
            let series = [];
            for (let i = 0; i < datasets.length; i++) {
                series.push(this.buildSerie(datasets[i].id, datasets[i].set));
            }

            return series;
        } else {
            return [this.buildSerie(datasets[0].id, datasets[0].set)];
        }
    }

    buildSerie(id, dataset) {
        let values = [];
        if (dataset !== undefined) {
            for (let t in dataset) {
                let value = [];
                if (dataset.hasOwnProperty(t)) {
                    value.push(Number(t));
                    let v = Number(dataset[t]);
                    value.push(v);
                }
                values.push(value);
            }
        }

        let yAxis = 0;
        if (
            this.getLastSelectedGraph().toLowerCase() === id.toLowerCase() &&
            this.selectedGraphs.length === 2 &&
            this.selectedGraph === 'time'
        ) {
            yAxis = 1;
        } else if (
            this.getLastSelectedGraph().toLowerCase() !== id.toLowerCase() &&
            this.selectedGraphs.length > 2 &&
            this.selectedGraph === 'time'
        ) {
            yAxis = 1;
        }

        let serie = {
            id: id.toLowerCase(),
            type: 'line',
            name: this.$t(`chart.${id.toLowerCase()}`),
            data: values,
            xAxis: 0,
            yAxis: yAxis,
            visible:
                this.visibleGraphs[id.toLowerCase()] !== undefined
                    ? this.visibleGraphs[id.toLowerCase()]
                    : false,
        };

        if (id.toLowerCase() === this.fvMotorName.toLowerCase()) {
            serie.linkedTo = this.fvName.toLowerCase();
            serie.yAxis = 0;
        }
        if (id.toLowerCase() === this.tvMotorName.toLowerCase()) {
            serie.linkedTo = this.tvName.toLowerCase();
            serie.yAxis = 0;
        }

        return serie;
    }

    resetGraph() {
        this.selectedGraph = 'time';
        this.visibleGraphs = {movement: true};
        this.selectedGraphs = ['movement'];
        this.lastSelectedGraph = 'movement';
    }

    @Watch('$route.params.token', {deep: true})
    shareTokenChanged() {
        const token = this.$route.params.token;
        if (token) {
            localStorage.setItem('share_token', token);
            this.$store.dispatch('getSharedScenario', token);
        }
    }

    /**
     * Load a scenario object
     */
    @Watch('scenarios')
    @Watch('shared')
    getScenario() {
        if (this.$route.path.indexOf('share') > -1) {
            //Fetch the scenario.
            if (this.shared !== undefined) {
                this.scenario = Scenario.fromJson(this.shared, this.unit);

                this.isShared = true;
                this.sharedBy = this.shared.shared_by;
                this.sharedByEmail = this.shared.shared_by_email;
            } else {
                this.isShared = false;
                this.error(this.$t('profiles.notFoundSim'), 5000);
            }
        } else {
            this.isShared = false;
            let id = this.scenarioID;
            if (id === -1) {
                this.scenario = Scenario.fromJson(this.newScenario, this.unit);

                if (this.shouldSelectMotor !== undefined) {
                    this.selectMotor(this.shouldSelectMotor);
                } else {
                    this.selectMotorIfExists();
                }
            } else {
                let json = this.$store.getters.getScenario(id);
                this.scenario = Scenario.fromJson(json, this.unit);

                if (json === undefined && this.scenarios.length > 0) {
                    this.error(this.$t('profiles.notFoundScen'), 5000);
                }
            }
        }

        this.saveOrder();

        this.hasChanges = false;

        if (
            this.scenario.motor === undefined ||
            (this.scenario.motor instanceof Motor && this.scenario.motor.isEmpty()) ||
            Object.keys(this.scenario.motor).length <= 0
        ) {
            this.selectMotor(this.selectMotorByName('tb12n'));
        } else {
            this.selectMotor(this.scenario.motor);
        }

        if (this.$store.getters.getMotorTypeById(this.scenario.motor.serie) !== undefined) {
            this.serie = this.$store.getters.getMotorTypeById(this.scenario.motor.serie);
        } else {
            this.serie = null;
        }

        this.$store.dispatch('activateScenario', this.scenarioID > 0 ? this.scenarioID : 0);
        this.$eventHub.$emit('activate-scenario', {
            scenario: this.scenario,
            id: this.scenarioID,
        });
        // this.$set(this.scenario.profiles, 'profiles', []);
        this.setGradients();

        if (this.serie !== null) {
            this.selectedMotorType = this.serie;
            this.selectedMotor = this.scenario.motor;
        }

        if (this.motorSelected) {
            this.scenario.convert(this.getCorrectUnit());
        }

        setTimeout(() => {
            this.simulate();
        }, 500);

        this.scenario.listen(obj => {
            this.hasChanges = true;
            this.simulate();
        });

        this.resetGraph();
        this.showShareInformation = this.isShared;
    }

    recheckRtherm() {
        const currentRth = this.scenario.applicationData.rtherm_heatsink;
        this.scenario.applicationData.recomputeRthermHeatsink();
        const recomputedRth = this.scenario.applicationData.rtherm_heatsink;
        if (currentRth !== recomputedRth) {
            // Rth has been updated with the simulation results, simulate again
            this.simulate();
        }
    }

    getCorrectUnit() {
        if (this.scenario.isTorque()) {
            if (this.unit.indexOf('(') > -1) {
                return this.unit;
            } else if (localStorage.getItem('torque_unit')) {
                return localStorage.getItem('torque_unit');
            }
        } else {
            if (this.unit.indexOf('(') < 0) {
                return this.unit;
            } else if (localStorage.getItem('unit')) {
                return localStorage.getItem('unit');
            }
        }
    }

    @Watch('id')
    scenarioIdChanged() {
        this.$store.dispatch('activateScenario', this.scenarioID);
        this.getScenario();
    }

    // @Watch('unit')
    unitChanged(unit) {
        if (unit === undefined) {
            unit = this.unit;
        }

        this.selected_unit = unit;

        this.scenario.convert(unit);
        this.newProfile.convert(unit);

        this.update();

        this.simulate();
    }

    toggleApplicationData() {
        this.cancelAdd();
        this.editAppData = !this.editAppData;
    }

    toggleMotor() {
        this.changeMotor = !this.changeMotor;
    }

    modalMotorSelection() {
        this.motorSelection = !this.motorSelection;
    }

    select(profile) {
        this.cancelAdd();

        if (this.selectedProfile.order !== profile.order) {
            this.selectedAppData = false;
            this.selectedProfile = profile;
            this.selected = true;
        } else {
            this.selectedAppData = false;
            this.selected = false;
            this.selectedProfile = {};
        }
    }

    selectAppData() {
        if (!this.selectedAppData) {
            this.selectedAppData = true;
            this.selected = true;
            this.selectedProfile = {};
        } else {
            this.selectedAppData = false;
            this.selected = false;
            this.selectedProfile = {};
        }
    }

    deselect() {
        this.selected = false;
        this.selectedAppData = false;
        this.selectedProfile = {};
        this.cancelEdit();
    }

    editParams() {
        if (this.selectedAppData) {
            this.toggleApplicationData();
        } else {
            this.edit = !this.edit;
        }
    }

    cancelEdit() {
        this.edit = false;
    }

    isSelected(profile) {
        return profile === this.selectedProfile;
    }

    changeOrder() {
        this.draggable = true;
        this.dragProfiles = JSON.parse(JSON.stringify(this.scenario.profiles));

        this.originalOrder = this.scenario.profiles.map(item => {
            return item.toJson();
        });
    }

    reorder({oldIndex, newIndex}) {
        const movedItem = this.scenario.profiles.splice(oldIndex, 1)[0];
        this.scenario.profiles.splice(newIndex, 0, movedItem);
    }

    cancelOrder() {
        this.scenario.profiles = this.originalOrder.map(item => {
            return MovementProfile.fromJson(item);
        });
        this.originalOrder = [];
        this.dragProfiles = [];
        this.draggable = false;
    }

    saveOrder(sim = true) {
        for (let i = 0; i < this.scenario.profiles.length; i++) {
            this.scenario.profiles[i].order = i + 1;
        }

        this.scenario.profiles.sort((l, r) => {
            return l.order - r.order;
        });

        this.draggable = false;
        // this.scenario.profiles = this.dragProfiles;
        this.dragProfiles = [];
        this.originalOrder = [];

        if (sim) {
            this.simulate();
        }
    }

    addMovementProfile(e) {
        e.preventDefault();

        let profile = this.newProfile;
        if (profile.type) {
            profile.order = this.addIndex;

            if (this.addIndex > -1) {
                this.scenario.addMovementProfile(this.addIndex, profile);
            } else {
                this.scenario.pushMovementProfile(profile);
            }

            this.saveOrder();
            this.cancelAdd();
            this.setGradients();
        }
    }

    saveMovementProfile() {
        this.cancelEdit();
    }

    addAfter(index) {
        this.cancelEdit();

        this.editAppData = false;
        let newIndex = index > -1 ? index + 1 : -1;

        this.adding = true;
        this.addIndex = newIndex;

        this.newProfile.listen(obj => this.scenario.changed(obj));
    }

    cancelAdd() {
        this.newProfile = new MovementProfile();
        this.newProfile.convert(this.getCorrectUnit());

        this.adding = false;
        this.addIndex = -1;
    }

    cancelAppData() {
        this.editAppData = false;
        this.showLoadInertiaTool = false;
        this.showRthTool = false;
        this.showMaxJerkTool = false;
    }

    confirmDelete() {
        this.showDeleteConfirmation = true;
    }

    closeDeleteDialog() {
        this.showDeleteConfirmation = false;
    }

    closeEditAppData() {
        this.editAppData = false;
    }

    deleteProfile() {
        if (this.selected) {
            this.scenario.deleteMovementProfile(this.selectedProfile);

            this.deselect();
            this.cancelAdd();
            this.setGradients();
            this.closeDeleteDialog();
        }
    }

    /**
     * Set the gradients on the movement profiles wrapper if it overflows.
     */
    setGradients() {
        // let container = this.$refs.movementProfiles.$el;
        let container = this.$refs.movementProfiles;
        if (container !== undefined) {
            setTimeout(() => {
                let scrollDif = container.scrollWidth - container.clientWidth;
                this.hasGradientLeft = container.scrollLeft > 0;
                this.hasGradientRight = container.scrollLeft < scrollDif && scrollDif > 0;
            }, 250);
        }
    }

    openMotorSelector() {
        this.changeMotor = true;
    }

    closeMotorSelector() {
        this.changeMotor = false;
        // this.selectedMotorType = null;

        if (this.serie !== null) {
            this.selectedMotorType = this.serie;
            this.selectedMotor = this.scenario.motor;
        }
    }

    /**
     * Set the selected motor type (iron core, ironless, etc.)
     * @param type
     */
    selectMotorType(type) {
        this.selectedMotorType = type;
    }

    /**
     * Select a motor for the current scenario
     * @param motor
     */
    selectMotor(motor) {
        if (motor !== undefined && motor.name !== undefined) {
            if (this.scenario.isTorque() && !motor.name.toLowerCase().startsWith('q')) {
                this.resetGraphs = true;
            } else if (!this.scenario.isTorque() && motor.name.toLowerCase().startsWith('q')) {
                this.resetGraphs = true;
            }

            this.shouldSelectMotor = motor;
            if (!this.scenario._motor) {
                this.scenario._motor = Motor.fromJson(motor);
            } else if (!this.scenario.motor.equals(Motor.fromJson(motor))) {
                this.scenario.motor = Motor.fromJson(motor);
            }

            if (this.$store.getters.getMotorTypeById(this.scenario.motor.serie) !== undefined) {
                this.serie = this.$store.getters.getMotorTypeById(this.scenario.motor.serie);
            } else {
                this.serie = null;
            }

            if (this.serie !== null) {
                this.selectedMotorType = this.serie;
                this.selectedMotor = this.scenario.motor;
            }
            this.closeMotorSelector();

            this.$eventHub.$emit('motor-changed', {
                isTorque: this.scenario.isTorque(),
            });
            this.motorSelected = true;
            this.scenario.convert(this.getCorrectUnit());
            this.simulate();
        }
    }

    changeSerie() {
        this.selectedMotorType = null;
    }

    /**
     * Increase the value of one of the quick edit fields
     * @param type
     */
    increase(type) {
        if (this.selected) {
            switch (type) {
                case 'movingDistance':
                    this.selectedProfile.increaseDistance();
                    this.$refs.quickDist.value = this.selectedProfile.movingDistance;
                    break;
                case 'torqueDistance':
                    this.selectedProfile.increaseTorqueDistance();
                    this.$refs.quickDist.value = this.selectedProfile.torqueDistance;
                    break;
                case 'movingTime':
                    this.selectedProfile.increaseMovingTime();
                    this.$refs.quickMoving.value = this.selectedProfile.movingTime;
                    break;
                case 'accTime':
                    this.selectedProfile.increaseAccTime();
                    this.$refs.quickAcc.value = this.selectedProfile.accelerationTime;
                    break;
                case 'accelerationForce':
                    this.selectedProfile.increaseAcceleration();
                    this.$refs.quickAcceleration.value = this.selectedProfile.acceleration;
                    break;
                case 'accelerationTorque':
                    this.selectedProfile.increaseAccelerationTorque();
                    this.$refs.quickAcceleration.value = this.selectedProfile.accelerationTorque;
                    break;
                case 'maxSpeed':
                    this.selectedProfile.increaseMaxSpeed();
                    this.$refs.quickMaxSpeed.value = this.selectedProfile.maxSpeed;
                    break;
                case 'torqueSpeed':
                    this.selectedProfile.increaseTorqueSpeed();
                    this.$refs.quickTorqueSpeed.value = this.selectedProfile.torqueSpeed;
                    break;
                case 'period':
                    this.selectedProfile.increasePeriod();
                    this.$refs.quickPeriod.value = this.selectedProfile.period;
                    break;
                case 'movingMass':
                    this.scenario.applicationData.increaseMovingMass();
                    this.$refs.quickMovingMass.value = this.scenario.applicationData.movingMass;
                    break;
                case 'frictionForce':
                    this.scenario.applicationData.increaseFrictionForce();
                    this.$refs.quickFrictionForce.value = this.scenario.applicationData.frictionForce;
                    break;
                case 'powerSupply':
                    this.scenario.applicationData.increasePowerSupply();
                    this.$refs.quickPowerSupply.value = this.scenario.applicationData.quickEditPowerSupply;
                    break;
                case 'friction':
                    this.scenario.applicationData.increaseFriction();
                    this.$refs.quickFriction.value = this.scenario.applicationData.friction;
                    break;
                case 'loadInertia':
                    this.scenario.applicationData.increaseLoadInertia();
                    this.$refs.quickLoadInertia.value = this.scenario.applicationData.loadInertia;
                    break;
            }
        }
    }

    /**
     * Decrease the value of one of the quick edit fields
     * @param type
     */
    decrease(type) {
        if (this.selected) {
            switch (type) {
                case 'movingDistance':
                    this.selectedProfile.decreaseDistance();
                    this.$refs.quickDist.value = this.selectedProfile.movingDistance;
                    break;
                case 'torqueDistance':
                    this.selectedProfile.decreaseTorqueDistance();
                    this.$refs.quickDist.value = this.selectedProfile.torqueDistance;
                    break;
                case 'movingTime':
                    this.selectedProfile.decreaseMovingTime();
                    this.$refs.quickMoving.value = this.selectedProfile.movingTime;
                    break;
                case 'accTime':
                    this.selectedProfile.decreaseAccTime();
                    this.$refs.quickAcc.value = this.selectedProfile.accelerationTime;
                    break;
                case 'accelerationForce':
                    this.selectedProfile.decreaseAcceleration();
                    this.$refs.quickAcceleration.value = this.selectedProfile.acceleration;
                    break;
                case 'accelerationTorque':
                    this.selectedProfile.decreaseAccelerationTorque();
                    this.$refs.quickAcceleration.value = this.selectedProfile.accelerationTorque;
                    break;
                case 'maxSpeed':
                    this.selectedProfile.decreaseMaxSpeed();
                    this.$refs.quickMaxSpeed.value = this.selectedProfile.maxSpeed;
                    break;
                case 'torqueSpeed':
                    this.selectedProfile.decreaseTorqueSpeed();
                    this.$refs.quickTorqueSpeed.value = this.selectedProfile.torqueSpeed;
                    break;
                case 'period':
                    this.selectedProfile.decreasePeriod();
                    this.$refs.quickPeriod.value = this.selectedProfile.period;
                    break;
                case 'movingMass':
                    this.scenario.applicationData.decreaseMovingMass();
                    this.$refs.quickMovingMass.value = this.scenario.applicationData.movingMass;
                    break;
                case 'frictionForce':
                    this.scenario.applicationData.decreaseFrictionForce();
                    this.$refs.quickFrictionForce.value = this.scenario.applicationData.frictionForce;
                    break;
                case 'powerSupply':
                    this.scenario.applicationData.decreasePowerSupply();
                    this.$refs.quickPowerSupply.value = this.scenario.applicationData.quickEditPowerSupply;
                    break;
                case 'loadInertia':
                    this.scenario.applicationData.decreaseLoadInertia();
                    this.$refs.quickLoadInertia.value = this.scenario.applicationData.loadInertia;
                    break;
                case 'friction':
                    this.scenario.applicationData.decreaseFriction();
                    this.$refs.quickFriction.value = this.scenario.applicationData.friction;
                    break;
            }
        }
    }

    closeSaveAsDialog() {
        this.saveAs = false;
        this.scenarioName = '';
    }

    /**
     * Update inputs with the current value in case of a unit change.
     */
    update() {
        if (this.selected && this.selectedProfile !== undefined) {
            this.onInputBlur(this.$refs.quickMoving, this.selectedProfile.movingTime);
            this.onInputBlur(this.$refs.quickAcc, this.selectedProfile.accelerationTime);
            if (this.scenario.isTorque()) {
                this.onInputBlur(
                    this.$refs.quickAcceleration,
                    this.selectedProfile.accelerationTorque,
                );
            } else {
                this.onInputBlur(this.$refs.quickAcceleration, this.selectedProfile.acceleration);
            }
            this.onInputBlur(this.$refs.quickMaxSpeed, this.selectedProfile.maxSpeed);
            this.onInputBlur(this.$refs.quickPeriod, this.selectedProfile.period);
        }

        //Update app data inputs
        this.updateTempHeatsink();
        this.updateMovingMass();
        this.updateFrictionForce();
        this.updatePowerSupply();
        this.updateInclinementAngle();

        //Update new movement profile dialog inputs
        this.updateNewAccelerationTime();
        this.updateNewLoad();
        this.updateNewMovingDistance();
        this.updateNewMovingTime();
    }

    /**
     * Determine if a certain field should be shown for a certain profile type
     * @param type
     * @param field
     * @returns {boolean}
     */
    shouldShowInput(type, field) {
        let arr = [];
        switch (field) {
            case 'movingDistance':
                arr = ['t3rd', 'm3rd', 'msin2', 'tsin2'];
                break;
            case 'movingTime':
                arr = ['t3rd', 'a3rd', 'asin2', 'cons', 'occ', 'tsin2', 'dwell', 'stay'];
                break;
            case 'accelerationTime':
                arr = ['t3rd', 'tsin2'];
                break;
            case 'maxVdi':
            case 'maxJerk':
                arr = ['t3rd', 'm3rd', 'a3rd'];
                break;
            case 'load':
                arr = [
                    't3rd',
                    'm3rd',
                    'msin2',
                    'tsin2',
                    'a3rd',
                    'asin2',
                    'q3rd',
                    'qsin2',
                    'occ',
                    'cons',
                ];
                break;
            case 'acceleration':
                arr = ['m3rd', 'msin2', 'q3rd', 'qsin2'];
                break;
            case 'maxSpeed':
                arr = ['m3rd', 'msin2', 'a3rd', 'asin2', 'q3rd', 'qsin2', 'occ', 'cons'];
                break;
            case 'period':
                arr = ['occ'];
                break;
        }

        return arr.indexOf(type) > -1;
    }

    shouldShowQuickEdit(type, field) {
        let arr = [];
        switch (field) {
            case 'movingDistance':
                arr = ['t3rd', 'm3rd', 'msin2', 'tsin2'];
                break;
            case 'movingTime':
                arr = ['t3rd', 'a3rd', 'asin2', 'cons', 'occ', 'tsin2', 'dwell', 'stay'];
                break;
            case 'accelerationTime':
                arr = ['t3rd', 'tsin2'];
                break;
            case 'maxVdi':
                arr = [];
                break;
            case 'load':
                arr = [];
                break;
            case 'acceleration':
                arr = ['m3rd', 'msin2', 'q3rd', 'qsin2'];
                break;
            case 'maxSpeed':
                arr = ['m3rd', 'msin2', 'a3rd', 'asin2', 'q3rd', 'qsin2', 'occ', 'cons'];
                break;
            case 'period':
                arr = ['occ'];
                break;
        }

        return arr.indexOf(type) > -1;
    }

    resultHasError(level, object) {
        if (object === undefined) {
            return false;
        }

        return object.warn_level === level;
    }

    /**
     * Check if all app data has been entered.
     */
    verifyAppData() {
        let error = false;
        if (
            this.scenario.applicationData.tempHeatsink === '' ||
            this.scenario.applicationData.movingMass === '' ||
            this.scenario.applicationData.frictionForce === '' ||
            this.scenario.applicationData.rtherm_heatsink === undefined ||
            this.scenario.applicationData.inclinementAngle === '' ||
            this.scenario.applicationData.powerSupply === ''
        ) {
            error = true;
        }

        this.appDataValidationError = error;

        if (!error) {
            this.cancelAppData();
        }
    }

    warn(text, duration = 5000) {
        this.warning = text;

        setTimeout(() => {
            this.warning = undefined;
        }, duration);
    }

    error(text, duration = 5000) {
        this.errorMessage = text;

        setTimeout(() => {
            this.errorMessage = undefined;
        }, duration);
    }

    success(text, duration = 5000) {
        this.successMessage = text;

        setTimeout(() => {
            this.successMessage = undefined;
        }, duration);
    }

    successCta(text, duration = 5000) {
        this.ctaMessage = text;

        setTimeout(() => {
            this.ctaMessage = undefined;
        }, duration);
    }

    getShareDialog() {
        if (this.scenario.motor.isEmpty()) {
            this.warn(this.$t('common.modals.warnMessages.sceneEmpty'));
        } else if (this.scenario.id) {
            this.share = true;
            this.shareEmail = null;
            this.shareMessage = null;
        } else {
            this.warn(this.$t('common.modals.warnMessages.saveScene'), 5000);
        }
    }

    closeShareDialog() {
        this.share = false;
        this.shareEmail = null;
        this.shareMessage = null;
        this.requestingShare = false;
    }

    shareScenario() {
        if (this.scenario.motor.isEmpty()) {
            this.warn(this.$t('common.modals.warnMessages.sceneEmpty'));
        } else if (
            this.scenario.id &&
            this.shareEmail !== undefined &&
            this.shareEmail.length > 0
        ) {
            if (this.emailPattern.test(this.shareEmail)) {
                this.requestingShare = true;
                Scenario.share(this.scenario.id, this.shareEmail, this.shareMessage)
                    .then(() => {
                        this.success(
                            this.$t('common.modals.successMessages.scenarioBeenShared', {
                                shareEmail: this.shareEmail,
                            }),
                        );
                        this.closeShareDialog();
                    })
                    .catch(err => {
                        this.error(this.$t('common.errorTryAgain'), 5000);
                        console.warn('Quote error', err);
                    });
            } else {
                this.error(this.$t('common.errorEmailNotValid'));
            }
        } else {
            this.warn(this.$t('profiles.saveScenMess'), 5000);
        }
    }

    getHelpDialog() {
        if (this.scenario.motor.isEmpty()) {
            this.warn(this.$t('common.modals.warnMessages.sceneEmpty'));
        } else if (this.scenario.id) {
            this.help = true;
            this.helpMessage = undefined;
        } else {
            this.warn(this.$t('common.modals.warnMessages.saveScene'), 5000);
        }
    }

    closeHelpDialog() {
        this.help = false;
        this.helpContact = null;
        this.helpMessage = null;
        this.requestingHelp = false;
    }

    getHelp() {
        if (this.scenario.motor.isEmpty()) {
            this.warn(this.$t('common.modals.warnMessages.sceneEmpty'));
        } else if (this.scenario.id) {
            this.requestingHelp = true;
            Scenario.help(this.scenario.id, this.helpMessage, this.helpContact)
                .then(() => {
                    this.closeHelpDialog();
                    this.success(this.$t('common.modals.successMessages.weWillHelp'));
                })
                .catch(err => {
                    this.error(this.$t('common.errorTryAgain'), 5000);
                    console.warn('Quote error', err);
                });
        } else {
            this.warn(this.$t('common.modals.warnMessages.saveScene'), 5000);
        }
    }

    getQuoteDialog() {
        this.showQuoteDialog = true;
        this.selectedScenario = this.scenarioID;
    }

    closeQuoteDialog() {
        this.showQuoteDialog = false;
        this.selectedScenario = -1;
        this.$eventHub.$emit('request-quote-complete');
    }

    quote() {
        if (this.selectedScenario !== -1) {
            this.requestingQuote = true;
            Scenario.quote(this.selectedScenario, this.quoteMessage)
                .then(() => {
                    this.requestingQuote = false;
                    this.closeQuoteDialog();
                    this.success(this.$t('common.modals.successMessages.contactShortly'), 5000);
                })
                .catch(err => {
                    this.error(this.$t('common.errorTryAgain'), 5000);
                    console.warn('[ quote ] Something went wrong.', err);
                });
        } else {
            this.warn(this.$t('common.modals.warnMessages.selectScenario'));
            this.closeQuoteDialog();
        }
    }

    get shouldShowResults() {
        return this.serie !== undefined && this.serie !== null && this.scenario.profiles.length > 0;
    }

    reset() {
        this.cancelEdit();
        this.cancelAdd();
        this.deselect();

        this.hasChanges = false;
        this.isShared = false;
    }

    routeUpdate(to, from, next) {
        if (this.hasChanges && !this.scenario.motor.isEmpty()) {
            this.navigationNext = next;
            this.showNavigationDialog = true;
        } else {
            this.reset();
            next();
        }
    }

    routeLeave(to, from, next) {
        if (this.hasChanges && !this.scenario.motor.isEmpty()) {
            this.navigationNext = next;
            this.showNavigationDialog = true;
        } else {
            this.reset();
            next();
        }
    }

    closeNavigationDialog() {
        this.navigationNext = undefined;
        this.showNavigationDialog = false;
    }

    confirmNavigation() {
        this.reset();
        this.navigationNext();
        this.closeNavigationDialog();

        this.$eventHub.$emit('navigated');
    }

    parseQueryString(query) {
        let vars = query.split('&');
        let query_string = {};
        for (let i = 0; i < vars.length; i++) {
            let pair = vars[i].split('=');
            // If first entry with this name
            if (typeof query_string[pair[0]] === 'undefined') {
                query_string[pair[0]] = decodeURIComponent(pair[1]);
                // If second entry with this name
            } else if (typeof query_string[pair[0]] === 'string') {
                let arr = [query_string[pair[0]], decodeURIComponent(pair[1])];
                query_string[pair[0]] = arr;
                // If third or later entry with this name
            } else {
                query_string[pair[0]].push(decodeURIComponent(pair[1]));
            }
        }
        return query_string;
    }

    selectMotorByName(name) {
        if (name === null || name === undefined || name.length === 0) {
            return {};
        }

        let types = this.motors;
        if (types !== undefined && types.length > 0) {
            for (let i = 0; i < types.length; i++) {
                let type = types[i];

                for (let j = 0; j < type.motors.length; j++) {
                    let motor = type.motors[j];

                    if (motor.name.toLowerCase() === name.toLowerCase()) {
                        return motor;
                    }
                }
            }
        }

        return {};
    }

    @Watch('motors')
    selectMotorIfExists() {
        let series = this.motors;
        let query = window.location.search;
        query = query.replace('?', '');
        let params = this.parseQueryString(query);

        if (
            this.scenario !== undefined &&
            (this.serie === undefined || this.serie === null) &&
            params.motor === undefined
        ) {
            this.selectMotor(this.scenario.motor);

            return;
        }

        if (series && series.length > 0 && this.scenario && !this.searched) {
            let searchMotor = '';
            if (this.scenarioID === -1 && this.scenario.motor.isEmpty()) {
                //Check if share token is passed
                let query = window.location.search;
                query = query.replace('?', '');
                let params = this.parseQueryString(query);

                let searchMotor;
                if (query !== undefined && query.length > 0 && params.motor) {
                    searchMotor = params.motor;

                    let motor = searchMotor.toLowerCase();

                    let series = this.motors;
                    for (let i = 0; i < series.length; i++) {
                        let serie = series[i];

                        for (let j = 0; j < serie.motors.length; j++) {
                            let m = serie.motors[j];

                            if (motor === m.name.toLowerCase()) {
                                this.selectMotor(m);
                                this.searched = true;
                                return;
                            }
                        }
                    }

                    this.searched = true;
                    this.error(this.$t('profiles.errorNotFoundMotor'), 7000);
                }
            }
        }
    }

    duplicate() {
        let p = this.selectedProfile;
        let copy = p.copy();

        let index = this.scenario.profiles.indexOf(p);
        if (index > -1) {
            this.addIndex = index + 1;

            if (this.addIndex > -1) {
                this.scenario.addMovementProfile(this.addIndex, copy);
            } else {
                this.scenario.pushMovementProfile(copy);
            }

            this.saveOrder(false);
            this.cancelAdd();
            this.setGradients();

            this.selectedProfile = copy;
        }
    }

    /**
     * Get a movement profile image based on the type
     */
    getImage(type, measurement) {
        return '/img/' + type + '-' + measurement + '.svg';
    }

    closeShareInformation() {
        this.showShareInformation = false;
    }

    getMotionProfileName(name) {
        if (this.scenario.isTorque()) {
            return name.replace('force', 'torque').replace('Force', 'Torque');
        }

        return name.replace('torque', 'force').replace('Torque', 'Force');
    }

    get rthToolAvailable() {
        const currentSeries = this.$store.getters.getMotorTypeById(this.scenario.motor.serie);
        // hide rth tool for vacuum series
        return !currentSeries.name.toLowerCase().includes('vacuum');
    }

    enableRthTool() {
        this.showRthTool = true;
        this.scenario.applicationData.rthermToolUsed = true;
    }

    enableMaxJerkTool() {
        this.update();
        this.showMaxJerkTool = true;
        this.scenario.applicationData.maxkJerkToolUsed = true;
    }

    sendToHubspot(link) {
        const data = {
            link: link.slice(link.lastIndexOf('/') + 1) + '_simtool',
            email: JSON.parse(this.getCookie('tecnotion_user')).email,
        };
        Vue.api.post('/download').send(JSON.stringify(data));
    }

    getCookie(name) {
        var match = document.cookie.match(new RegExp(name + '=([^;]+)'));
        if (match) return match[1];
    }

    showLoadInertiaTool = false;
    showRthTool = false;
    showMaxJerkTool = false;
}
